import { useEffect, useState } from 'react';
export function propertyAtCaret(text, caret) {
    const prefix = text.slice(0, Math.max(0, caret));
    const match = /(?:^|\s)#([^\r\n#@]*)$/.exec(prefix);
    return match ? { query: match[1].trim(), start: prefix.length - match[1].length - 1, end: prefix.length } : undefined;
}
/** Query keys keep stale pages out of a new conversation or address prefix. */
export function usePropertyReferences(request, text, caret, scope, enabled) {
    const identity = `${scope}\0${text}\0${caret}`;
    const [dismissed, setDismissed] = useState('');
    const token = enabled && dismissed !== identity ? propertyAtCaret(text, caret) : undefined;
    const query = token?.query;
    const key = `${scope}\0${query ?? '\0'}`;
    const [cursor, setCursor] = useState({ key: '', after: 0 });
    const after = cursor.key === key ? cursor.after : 0;
    const [revision, setRevision] = useState(0);
    const [state, setState] = useState({ key: '', rows: [], loading: false, error: '', more: false, next: 0 });
    useEffect(() => { setCursor({ key, after: 0 }); }, [key]);
    useEffect(() => {
        if (query === undefined || cursor.key !== key)
            return;
        const abort = new AbortController();
        setState(current => ({ key, rows: current.key === key ? current.rows : [], loading: true, error: '', more: current.key === key && current.more, next: current.key === key ? current.next : 0 }));
        const timer = setTimeout(() => {
            void request(`/reference-properties?q=${encodeURIComponent(query)}&after_id=${after}&limit=50`, abort.signal)
                .then(page => {
                if (abort.signal.aborted)
                    return;
                setState(current => ({ key, rows: [...new Map([...(after && current.key === key ? current.rows : []), ...page.properties].map(row => [row.id, row])).values()], loading: false, error: '', more: page.has_more, next: page.next_after_id }));
            })
                .catch(cause => {
                if (!abort.signal.aborted)
                    setState(current => ({ ...current, loading: false, error: cause instanceof Error ? cause.message : 'Unable to load properties.' }));
            });
        }, after ? 0 : 250);
        return () => { abort.abort(); clearTimeout(timer); };
    }, [request, query, key, cursor.key, after, revision]);
    const current = state.key === key ? state : { rows: [], loading: true, error: '', more: false, next: 0 };
    return { token, rows: token ? current.rows : [], loading: Boolean(token && current.loading), error: token ? current.error : '', more: Boolean(token && current.more),
        loadMore: () => { if (current.more && !current.loading)
            setCursor({ key, after: current.next }); },
        retry: () => setRevision(value => value + 1), dismiss: () => setDismissed(identity) };
}
